import { useState } from "react";
import { useNossaHistoria, TeamMember } from "@/hooks/useNossaHistoria";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2, Edit2, Plus, Save, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function TeamManager() {
  const { teamMembers, teamLoading, updateTeamMember, createTeamMember, deleteTeamMember } = useNossaHistoria();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<TeamMember>>({
    name: '',
    position: '',
    description: '',
    photo_url: '',
    is_active: true,
    display_order: 0
  });

  const handleEdit = (member: TeamMember) => {
    setEditingId(member.id);
    setFormData(member);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      name: '',
      position: '',
      description: '',
      photo_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=400&h=400&fit=crop&crop=face',
      is_active: true,
      display_order: (teamMembers?.length || 0)
    });
  };

  const handleSave = () => {
    if (!formData.name || !formData.position || !formData.description || !formData.photo_url) {
      return;
    }

    if (isCreating) {
      createTeamMember(formData as Omit<TeamMember, 'id'>);
      setIsCreating(false);
    } else if (editingId) {
      updateTeamMember({ id: editingId, updates: formData });
      setEditingId(null);
    }
    
    setFormData({
      name: '',
      position: '',
      description: '',
      photo_url: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      name: '',
      position: '',
      description: '',
      photo_url: '',
      is_active: true,
      display_order: 0
    });
  };

  if (teamLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Membros da Equipe</h3>
        <Button onClick={handleCreate} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Membro
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle>{isCreating ? 'Novo Membro' : 'Editar Membro'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nome (2-100 caracteres)</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={100}
                placeholder="Ex: Dra. Brenda Ramos"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.name?.length || 0}/100
              </p>
            </div>

            <div>
              <Label>Cargo (2-200 caracteres)</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                maxLength={200}
                placeholder="Ex: Nutricionista - CRN 3/63492"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.position?.length || 0}/200
              </p>
            </div>

            <div>
              <Label>Descrição (10-500 caracteres)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
                rows={4}
                placeholder="Descrição do membro da equipe..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.description?.length || 0}/500
              </p>
            </div>

            <div>
              <Label>URL da Foto</Label>
              <Input
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                placeholder="https://..."
              />
              {formData.photo_url && (
                <div className="mt-2">
                  <img 
                    src={formData.photo_url} 
                    alt="Preview" 
                    className="w-20 h-20 rounded-full object-cover"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                min={0}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Ativo</Label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!formData.name || !formData.position || !formData.description || !formData.photo_url}>
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teamMembers?.map((member) => (
          <Card key={member.id}>
            <CardContent className="p-4">
              <div className="flex flex-col gap-3">
                <img 
                  src={member.photo_url} 
                  alt={member.name}
                  className="w-full aspect-square object-cover rounded-lg"
                />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{member.name}</h4>
                    {!member.is_active && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-primary mb-2">{member.position}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">{member.description}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ordem: {member.display_order}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(member)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (confirm('Tem certeza que deseja deletar este membro?')) {
                        deleteTeamMember(member.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}